import { useMutation, useQuery } from '@tanstack/react-query';

import type { ApiConfiguration, ApiError, PublicApi } from '@izimi/api';

import PublicQueryKeys from './PublicQueryKeys';

const STALE_30_MINUTES = 1800000;
const publicQueryKeys = new PublicQueryKeys();

const createPublicModule = ({ publicApi }: { publicApi: PublicApi }) => {
  const getConfiguration = (fetcher: () => Promise<ApiConfiguration>) => () => {
    const { data, isLoading, error, refetch } = useQuery<ApiConfiguration, ApiError>({
      queryKey: publicQueryKeys.getConfiguration(),
      queryFn: fetcher,
      staleTime: STALE_30_MINUTES,
    });

    return {
      configuration: data,
      error,
      isLoading,
      refetch,
    };
  };

  const useWebConfiguration = getConfiguration(publicApi.getWebConfiguration.bind(publicApi));
  const useNotaryConfiguration = getConfiguration(publicApi.getNotaryConfiguration.bind(publicApi));
  const usePortalConfiguration = getConfiguration(publicApi.getPortalConfiguration.bind(publicApi));
  const useMobileConfiguration = getConfiguration(publicApi.getConfiguration.bind(publicApi));

  const useMailUnsubscribeInfo = (token: string) => {
    const { data, error, isLoading } = useQuery({
      queryKey: publicQueryKeys.getMailUnsubscribeInfo(token),
      queryFn: () => publicApi.getMailUnsubscribeInfo(token),
    });

    return {
      data,
      error,
      isLoading,
    };
  };

  const useMailUnsubscribe = () => {
    const { mutateAsync, isPending } = useMutation<void, ApiError, string>({
      mutationFn: (token) => publicApi.mailUnsubscribe(token),
    });

    return {
      unsubscribe: mutateAsync,
      isPending,
    };
  };

  const useServiceInfos = () =>
    useQuery({
      queryKey: publicQueryKeys.getServiceInfos(),
      queryFn: () => publicApi.getVersion(),
    });

  return {
    useServiceInfos,
    useWebConfiguration,
    useNotaryConfiguration,
    usePortalConfiguration,
    useMobileConfiguration,
    useMailUnsubscribeInfo,
    useMailUnsubscribe,
  };
};

export default createPublicModule;
